import { useCallback } from "react";
import { useAtomValue } from "jotai";
import { Button, ErrorState, Spinner } from "@common/index";
import { ComponentColor } from "@common/types";
import { useOrganizationApi } from "@/hooks/apiHooks/useOrganizationApi";
import { useMountRead } from "@/hooks/useMountRead";
import { ROUTES } from "@routing/routes";
import { SUPPORT_EMAIL } from "@/config/supportConfig";
import { formatDate } from "@utils/formatter";
import { organizationsAtom } from "@/store/organizationAtoms";
import { currentUserAtom, userFetchErrorAtom } from "@/store/userAtoms";
import { AccountLoadError } from "@components/Auth/AccountLoadError";
import { SubscriptionStatus } from "@models/organization";
import { AddPaymentMethodPanel } from "@customer/PaymentRequired/AddPaymentMethodPanel";
import { ASK_ADMINISTRATOR } from "@customer/PaymentRequired/_helpers/askAdministrator";
import { isOrganizationAdmin } from "@customer/PaymentRequired/_helpers/isOrganizationAdmin";
import { MembershipStatusBadge } from "./MembershipStatusBadge";

// Allow-lists, deliberately not deny-lists: a status added to the enum later
// defaults to showing nothing rather than to asserting something about money.

// Only a live subscription has a next billing date. Claiming one for an
// Expired/Cancelled/Suspended membership is a false billing statement (#352).
const BILLING_DATE_STATUSES: SubscriptionStatus[] = [
    SubscriptionStatus.Active,
];

// Suspended is cancellable: PaymentRequiredPage sends suspended users here to act.
const CANCELLABLE_STATUSES: SubscriptionStatus[] = [
    SubscriptionStatus.Active,
    SubscriptionStatus.Suspended,
];

// Already over — the contact path is reactivation, not cancellation.
const ENDED_STATUSES: SubscriptionStatus[] = [
    SubscriptionStatus.Cancelled,
    SubscriptionStatus.Expired,
];

// Suspended ONLY — where #372 puts an org on the first declined renewal.
// Deliberately NOT Expired: ChargeDueMembershipsCommand selects Active or
// Suspended-with-a-non-zero-attempt-counter, so an Expired membership is never a
// charge candidate and telling that customer a card fixes it would be false.
// ENDED_STATUSES already owns Expired with the truthful "contact us to discuss
// reactivating". Allow-list for the same reason as the others — a status added
// later must not silently inherit a claim about money.
const PAYMENT_PROBLEM_STATUSES: SubscriptionStatus[] = [
    SubscriptionStatus.Suspended,
];

// Membership is ONE subscription per organization (#352 / #313) — never per
// location. Locations are listed by name only, with no status, period or
// action of their own, so nothing here can imply per-location membership.
export const MembershipTab = () => {
    const { getMeOrganizations } = useOrganizationApi();
    const organizations = useAtomValue(organizationsAtom);
    const currentUser = useAtomValue(currentUserAtom);
    const hasUserFetchError = useAtomValue(userFetchErrorAtom);
    // The status of the read THIS tab issued (#407), not app-wide atoms: those
    // outlive the tab, so a revisit painted the last visit's failure, and a
    // first visit painted "We couldn't find an organization" before anything
    // had been read. Wrapped so the `isCurrent` useMountRead hands a read never
    // reaches getMeOrganizations as its options.
    const readOrganizations = useCallback(
        () => getMeOrganizations(),
        [getMeOrganizations],
    );
    const { readStatus, reload: fetchOrganizations } =
        useMountRead(readOrganizations);

    // A user belongs to exactly one organization; the endpoint returns an array.
    const organization = organizations[0];
    const subscription = organization?.subscription;
    const locations = organization?.locations ?? [];

    // `cancelAtPeriodEnd` wins over status: a membership winding down states its
    // end date and offers neither contact path.
    const isEndingAtPeriodEnd = !!subscription && subscription.cancelAtPeriodEnd;
    const showBillingDate =
        !!subscription &&
        !isEndingAtPeriodEnd &&
        BILLING_DATE_STATUSES.includes(subscription.status);
    const showCancelContact =
        !!subscription &&
        !isEndingAtPeriodEnd &&
        CANCELLABLE_STATUSES.includes(subscription.status);
    const showReactivateContact =
        !!subscription &&
        !isEndingAtPeriodEnd &&
        ENDED_STATUSES.includes(subscription.status);
    // Status alone — deliberately WITHOUT the `!isEndingAtPeriodEnd` guard its three
    // siblings carry. A lapse is a lapse whatever the renewal flag says, and it is
    // the one thing on this page the customer can act on. A membership that is both
    // suspended and winding down therefore shows this notice AND the "set to end …
    // will not renew" line: both are true, they do not contradict, and that customer
    // still has a failed charge to fix for the period they have left. Not a bug —
    // there is a test pinning it.
    const showPaymentProblemNotice =
        !!subscription &&
        PAYMENT_PROBLEM_STATUSES.includes(subscription.status);

    // #368 PO ruling (2026-11-03): only the organization's Admin may save a card
    // (CreatePaymentMethod requires the organization Admin role), so only the
    // Admin is offered the card surface. From the organization this tab has
    // already read and the signed-in user: no read of its own.
    const isAdmin =
        !!currentUser && isOrganizationAdmin(organization, currentUser.id);

    return (
        <div className="flex flex-col gap-4">
            {/* First load only. A saved card fires onSaved -> fetchOrganizations,
                which re-reads while AddPaymentMethodPanel below is showing its
                success state — so swapping the whole body for a spinner on every
                read would tear that state down the instant the customer earned
                it. Once an organization is in hand the tab refreshes in place. */}
            {readStatus === "loading" && !organization && (
                <div className="flex justify-center items-center py-12">
                    <Spinner size="lg" />
                </div>
            )}

            {/* Only when there is nothing to show. A failed read LEAVES
                organizationsAtom populated, so an error that wins outright would
                replace a rendered card confirmation with an error page after the
                card was successfully stored — exactly what AddPaymentMethodPanel
                refuses to do with its own refresh failure. With an organization in
                hand, a failed refresh therefore leaves the existing data on screen:
                the card is already stored, the panel's own state is the truthful
                record of what just happened, and the tab refetches on next mount
                anyway. */}
            {readStatus === "failed" && !organization && (
                <ErrorState onRetry={fetchOrganizations} />
            )}

            {readStatus === "loaded" && !organization && (
                <div className="flex flex-col items-center gap-4 py-12 text-center">
                    <p className="text-neutral/70">
                        We couldn&apos;t find an organization on your account.
                    </p>
                    <Button
                        to={ROUTES.SUPPORT}
                        color={ComponentColor.Warning}
                        className="text-neutral font-bold normal-case"
                    >
                        Contact Support
                    </Button>
                </div>
            )}

            {/* Organization alone: see the error gate above — data already in hand
                outlives a failed refresh rather than being replaced by an error. */}
            {organization && (
                <>
                    <section className="rounded-lg border border-base-300 bg-base-200 p-4 flex flex-col gap-2">
                        <div className="flex items-center justify-between gap-4">
                            <h2 className="font-semibold text-neutral">
                                {organization.name}
                            </h2>
                            {subscription && (
                                <MembershipStatusBadge
                                    status={subscription.status}
                                />
                            )}
                        </div>

                        {/* PO ruling 2026-11-02: no subscription yet (e.g. an
                            admin-created client still in PendingPayment). The
                            API creates the subscription only after a membership
                            charge goes through, so that is the one promise made
                            here. Not "once you add a card": a card can already
                            be on file after a declined charge, and a card saved
                            while no location has passed review charges
                            nothing. */}
                        {!subscription && (
                            <div className="flex flex-col gap-1">
                                <h3 className="font-semibold text-neutral">
                                    Membership not set up yet
                                </h3>
                                <p className="text-sm text-neutral/70">
                                    Your membership starts once its first
                                    payment goes through.
                                </p>
                            </div>
                        )}

                        {subscription && (
                            <div className="text-sm text-neutral/80 flex flex-col gap-1">
                                <p>
                                    Plan:{" "}
                                    <span className="font-medium">
                                        {subscription.subscriptionPlanName}
                                    </span>
                                </p>
                                {isEndingAtPeriodEnd && (
                                    <p>
                                        Ends:{" "}
                                        <span className="font-medium">
                                            {formatDate(
                                                subscription.currentPeriodEnd,
                                            )}
                                        </span>
                                    </p>
                                )}

                                {showBillingDate && (
                                    <p>
                                        Next billing date:{" "}
                                        <span className="font-medium">
                                            {formatDate(
                                                subscription.currentPeriodEnd,
                                            )}
                                        </span>
                                    </p>
                                )}
                            </div>
                        )}
                    </section>

                    {/* The lapse explanation, immediately above the thing that acts on
                        it. It points at the card surface without promising a charge:
                        the charge job stops retrying at the attempt cap and never
                        touches an admin suspension at all, so "this is what lets the
                        next charge go through" is false on the rungs where the
                        customer most needs the truth. No schedule and no number of
                        attempts either — the UI cannot see the counter (§1.7/§1.8). */}
                    {/* "You can update your payment method below" is said to the
                        Admin only: nobody else has a card surface below (#368). */}
                    {showPaymentProblemNotice && (
                        <p className="text-sm text-neutral/80">
                            {[
                                "Your membership is paused after a problem with a payment.",
                                isAdmin &&
                                    "You can update your payment method below.",
                                "If your access isn't restored, contact support and we'll take it from there.",
                            ]
                                .filter(Boolean)
                                .join(" ")}
                        </p>
                    )}

                    {/* Outside the summary section on purpose — the panel is its own
                        <section> with its own "Payment Method" heading. Rendered
                        for the Admin whenever there is an organization, subscription
                        or not: an org awaiting its first activation has no
                        Subscription row at all, and gating on one would re-create the
                        dead end for exactly the customer who needs the card form.
                        The id is handed down rather
                        than re-fetched: this component has already read it. */}
                    {/* Read off the ORGANIZATION, not the subscription: a card is
                        persisted before activation runs, so an org can hold one with
                        no subscription row at all — the state a 402 after the card
                        was stored leaves behind (a failed card save stores
                        nothing), and the one this page exists to handle. */}
                    {/* The role needs the user as well as the organization, and
                        this tab sits outside ApprovalGate, where the user can
                        still be loading, or have failed to load: until it is
                        known, neither role's surface is shown (AccountProfile's
                        loading and failed states). Anyone but the Admin is told
                        to ask their administrator, to update the card on file or
                        to add one when there is none. */}
                    {!currentUser && hasUserFetchError && <AccountLoadError />}
                    {!currentUser && !hasUserFetchError && (
                        <div className="flex justify-center py-6">
                            <Spinner size="lg" />
                        </div>
                    )}
                    {currentUser && !isAdmin && (
                        <p className="text-sm text-neutral/80">
                            {organization.paymentMethod
                                ? ASK_ADMINISTRATOR.update
                                : ASK_ADMINISTRATOR.add}
                        </p>
                    )}
                    {isAdmin && (
                        <AddPaymentMethodPanel
                            variant="update"
                            currentCard={organization.paymentMethod ?? null}
                            organizationId={organization.id}
                            onSaved={fetchOrganizations}
                        />
                    )}

                    {/* Self-serve cancel is deliberately absent until self-serve
                        reactivation (#328) ships — cancelling is a support
                        conversation, not a button. */}
                    {isEndingAtPeriodEnd && subscription && (
                        <p className="text-sm text-neutral/80">
                            Your membership is set to end on{" "}
                            {formatDate(subscription.currentPeriodEnd)} and will
                            not renew.
                        </p>
                    )}

                    {showCancelContact && (
                        <section className="flex flex-col items-start gap-2">
                            <h2 className="font-semibold text-neutral">
                                Need to cancel?
                            </h2>
                            <p className="text-sm text-neutral/80">
                                To cancel your membership, contact us at{" "}
                                <a
                                    href={`mailto:${SUPPORT_EMAIL}`}
                                    className="link link-primary"
                                >
                                    {SUPPORT_EMAIL}
                                </a>{" "}
                                and our team will take care of it.
                            </p>
                            <Button
                                to={ROUTES.SUPPORT}
                                color={ComponentColor.Primary}
                                outline
                                className="normal-case"
                            >
                                Contact Support
                            </Button>
                        </section>
                    )}

                    {showReactivateContact && (
                        <section className="flex flex-col items-start gap-2">
                            <h2 className="font-semibold text-neutral">
                                Membership ended
                            </h2>
                            <p className="text-sm text-neutral/80">
                                Contact us at{" "}
                                <a
                                    href={`mailto:${SUPPORT_EMAIL}`}
                                    className="link link-primary"
                                >
                                    {SUPPORT_EMAIL}
                                </a>{" "}
                                to discuss reactivating your membership.
                            </p>
                            <Button
                                to={ROUTES.SUPPORT}
                                color={ComponentColor.Primary}
                                outline
                                className="normal-case"
                            >
                                Contact Support
                            </Button>
                        </section>
                    )}

                    {locations.length > 0 && (
                        <section className="flex flex-col gap-2">
                            {/* Deliberately NOT "Locations covered by this
                                membership": the list is unfiltered, so a
                                Rejected/inactive location would be asserted as
                                covered — smuggling the per-location membership
                                claim back in via the header. */}
                            <h2 className="font-semibold text-neutral">
                                Your locations
                            </h2>
                            <ul className="list-disc pl-5 text-sm text-neutral/80 flex flex-col gap-1">
                                {locations.map((location) => (
                                    <li key={location.id}>{location.name}</li>
                                ))}
                            </ul>
                        </section>
                    )}
                </>
            )}
        </div>
    );
};
